/**
 * Bynder metaproperties configuration.
 * Each entry maps a logical name → Bynder metaproperty UUID + (optional) fixed option UUID.
 *
 * For "select" / "select2" types the value sent to Bynder is the option UUID.
 * For "text" / "longtext" / "date" types the value is a plain string.
 *
 * Environment is selected via VITE_BYNDER_ENV (defaults to 'prod').
 */

type BynderMetaEntry = {id: string; optionId?: string}
type BynderMetaConfig = {
  sector: Required<BynderMetaEntry>
  country: Pick<BynderMetaEntry, 'id'>
  businessFunction: Required<BynderMetaEntry>
  rightsAvailable: Required<BynderMetaEntry>
  assetPurpose: Required<BynderMetaEntry>
  assetType: Required<BynderMetaEntry>
  auditStatus: Required<BynderMetaEntry>
  brand: Required<BynderMetaEntry>
  subBrand: Required<BynderMetaEntry>
  globalCampaign: Required<BynderMetaEntry>
  agencyName: Required<BynderMetaEntry>
  keyContactDetails: Pick<BynderMetaEntry, 'id'>
  usageTerms: Pick<BynderMetaEntry, 'id'>
  inMarketDate: Pick<BynderMetaEntry, 'id'>
  expiryDate: Pick<BynderMetaEntry, 'id'>
  integrationSourceId: Pick<BynderMetaEntry, 'id'>
  assetSource: Pick<BynderMetaEntry, 'id'>
}

// ─── Production ──────────────────────────────────────────────
const BYNDER_META_PROD: BynderMetaConfig = {
  // select
  sector: {id: 'B81C8B07-7952-4E04-B8FFC74B7CFE882F', optionId: '7C8036EA-53B5-4777-8876BDA731769503'},
  country: {id: '207A86C3-B88C-40E0-A150F38A583AA0FC'},
  businessFunction: {id: '34FEB745-4924-4F01-853F28843AFEB662', optionId: '8EB8EE66-4BB0-45DD-841BD118ED899885'},
  rightsAvailable: {id: '8A5F43C2-6FE3-4153-82AAF5F14AC71EF6', optionId: '6A2313E8-39A7-48AD-B5674C03E8D05786'},
  assetPurpose: {id: 'B5D25151-E671-4A5D-A6ADA20D1A5518FA', optionId: '9CBC41E2-A71B-4FD9-BEC7A581A2919F85'},
  assetType: {id: '8B05898A-0826-4E23-99487EC575CA9198', optionId: '9FCE2B8B-3A59-45ED-89C5B83BD36DFD34'},
  auditStatus: {id: 'FBFBCCEE-96BA-4B08-A8796A0F009285CC', optionId: 'F5CA6126-EBD2-412A-A023ED4334F7B8C7'},
  // select2
  brand: {id: '30F5DFD4-5BD1-4348-A9303355422D1B0D', optionId: '508C466D-9F30-4DFE-8F89D215C1027357'},
  subBrand: {id: 'E60F4B6F-7706-4066-87074FDCECC72DBA', optionId: '092A3EEA-1A32-46AC-8DCC104BD38536CA'},
  globalCampaign: {id: 'D1522F83-2410-4034-89D6439B76C51B41', optionId: '1681386A-8471-47F3-9A01704259FCC802'},
  agencyName: {id: 'C824A93E-2577-4457-A285AAB2BE482EB5', optionId: '2377D971-D2CB-40EF-AD6BC516E0D1E5A3'},
  // text / longtext
  keyContactDetails: {id: '6E8D3DDD-36EB-4425-BB81F71189866799'},
  usageTerms: {id: '66F55A50-DCFC-470F-B5404103E8552D01'},
  // date
  inMarketDate: {id: '27A0EA22-2927-4D65-AAD51B487CE3F8CC'},
  expiryDate: {id: '21E78CCA-7A19-4408-A525F883CDB8101B'},
  // other
  integrationSourceId: {id: 'F353585F-9AB1-4EDE-8E11F7C6049D890E'},
  assetSource: {id: 'EAD41C19-3B97-49AE-9B75A615F27108F8'},
}

// ─── QA ──────────────────────────────────────────────────────
const BYNDER_META_QA: BynderMetaConfig = {
  // select
  sector: {id: '037F8052-BDC0-4EFE-ADE37EEA811B1E15', optionId: 'A2B18828-E454-449B-96BA71A13ED5F4FE'},
  country: {id: 'AF9F93EE-FD6F-4BEF-AE6412C01D12B77A'},
  businessFunction: {id: '849DE891-C28D-4015-B2DC9638F3C1788F', optionId: 'B08504D3-43DD-4B59-BD922E7E67CA761F'},
  rightsAvailable: {id: 'BEA50367-3D7E-4808-833220A2E0938FC0', optionId: '7C8D4C2D-AE1E-4F16-A5AC1BDE9DE4B105'},
  assetPurpose: {id: '3458A69F-A711-4E7D-934EBE6B52677E7D', optionId: '8E60C272-B142-426C-91748BDF1E0DD591'},
  assetType: {id: 'A121BEE5-15E1-4DB4-BEF12B71258B6A02', optionId: '30DB4C07-5366-4FB6-A9A6AD746FDA03F0'},
  auditStatus: {id: 'A1FC69FE-010B-4779-BA502011BA90A803', optionId: '1D53800D-C688-4D6E-AC9A33EF15DC785A'},
  // select2
  brand: {id: '34236653-0DCE-4D8D-93CF13E94CB1486B', optionId: '395DB4B3-CD3F-4735-BA37EAEA6AB3743C'},
  subBrand: {id: '2CB194DC-13D6-4108-BADCE4902F2EBF30', optionId: 'EF1F427D-C7D8-4A75-9FFA7EF11B065BD1'},
  globalCampaign: {id: '85ADA1F6-E7D8-43D1-8B27EEDE0A44F9C5', optionId: 'A70399EE-579D-4914-A2A35C80D6A8E401'},
  agencyName: {id: '6C749EAB-F42C-4BA3-B735A8B6B9516893', optionId: '7DDDDC47-6FE2-4BB9-926EC75BCF0414E0'},
  // text / longtext
  keyContactDetails: {id: '624ECE69-7E8C-4467-A638C482FCD21BC9'},
  usageTerms: {id: '3FCA5F0E-90CB-4F6F-AF2BB14485F33DD3'},
  // date
  inMarketDate: {id: 'ED637C44-E558-434A-A86CA8B5BB0FADDB'},
  expiryDate: {id: '6627206C-BFB8-4745-B76C5A79A532FB60'},
  // other
  integrationSourceId: {id: ''},
  assetSource: {id: '4CA35777-387B-4D98-8297FA7F8ABCEBDB'},
}

// ─── Environment resolution ─────────────────────────────────
const BYNDER_ENV_MAP: Record<string, BynderMetaConfig> = {
  prod: BYNDER_META_PROD,
  qa: BYNDER_META_QA,
}

const bynderEnv = import.meta.env.VITE_BYNDER_ENV || 'prod'
export const BYNDER_META = BYNDER_ENV_MAP[bynderEnv] ?? BYNDER_META_PROD

/** Shorthand for `metaproperty.<uuid>` key used by the Bynder upload API. */
export const metaKey = (uuid: string) => `metaproperty.${uuid}`

/** Maps ISO-2 country codes to Bynder Country metaproperty option UUIDs. */
const BYNDER_COUNTRY_OPTIONS_PROD: Record<string, string> = {
  AF: '8B4C9FFD-50CD-4922-9A2B1D81BE98E43F',
  AL: 'E03A7185-7A24-49A8-91D4CD195C4FE160',
  DZ: 'E9C1EB1D-FFFC-4217-A30234A6C2152AEF',
  AO: '3754F1EF-0B58-47F2-B77552B03F8711C2',
  AI: 'E89F3FDC-F5D3-420C-BED5433AEF330380',
  AG: 'AAECB443-3305-4128-B1161751E5344F4A',
  AR: '600EAD4D-D807-4A1E-95B2B465DE1BD9E5',
  AM: '5D5C53B8-EF5C-42DC-8EF37B72A4AB5647',
  AW: 'E46108E5-B5D7-481E-B6C5D95D34CFA121',
  AU: 'A8FB7E22-19FC-407E-98770F9F2AD28B85',
  AT: 'ED2BBB56-9263-4F7C-A182791A95B06279',
  AZ: 'C28ED1B9-CA5E-457C-BBE530EB32D493C0',
  BS: '9ED5C438-0576-4EB4-B148E0840A6A5BC1',
  BH: '568A78F4-2F9D-4B19-A7BC0AA6F38C9507',
  BD: '66CABCF9-29C2-4D13-A41D7F3F19D9CD2E',
  BB: 'A4630452-3366-46AF-A3F49881F2CB2DCE',
  BY: 'CCACE7CC-DD72-4518-B1166FF4DB9B0DD1',
  BE: '009B0A96-7163-4919-B268429E3DA4C6E7',
  BZ: '71901489-0D6F-4FB3-AC23A32FCFD38C41',
  BM: '53072BB0-BB18-49B2-AEB1EDE3E466B0B8',
  BO: '841A62AC-B599-4AFF-BC4FE310A7407F53',
  BA: 'DB11A269-4C89-4D4D-807A589D30D2820D',
  BR: '1322936E-0C0E-4CDE-92F0B7EA9CFEC163',
  BN: '0159871E-E73E-4CD2-9FFF8B9F3160030A',
  BG: 'BAFD2CF8-6389-4286-AE7F894A5DD264CF',
  MM: '8342E316-CF48-465A-9BBF4B171658D94C',
  KH: 'BDC3977A-7CAB-40F3-9A009057CF78DAFD',
  CM: '9A029D50-3EBB-4446-ADB6446B53B6B6BE',
  CA: '892B1919-5789-4F7C-926300D822230138',
  KY: '7E225AD9-4840-4DB1-B592D4F4DF15E894',
  CL: '17729062-E5B5-4C03-918A37B4025E4214',
  CN: 'F01CA2EA-AB8D-48A1-9237B2285C74C66F',
  CO: '22D89B74-39B3-4F3C-AF2CCD47DEBF288B',
  CR: '5D9603E0-A626-4983-ACC2B9944F38844C',
  HR: 'C63AF733-46AC-47AD-863073FE0BFB8FFE',
  CW: 'BC554B0D-B209-4F96-B371374DA2E22F9E',
  CY: 'AD48411A-07A7-41BC-899C400F739BD36E',
  CZ: 'A32633CB-89EA-4B29-82430F1651808534',
  DK: '17DA5C9E-C3BB-4970-863412ECB0FFF112',
  DJ: '5CFE27DC-961D-4BA1-AF67222FE8831E3B',
  DM: 'E6BCF097-735B-49EA-8C1BB21D195E7D00',
  DO: '4C6AE660-9E90-42AA-AB934B53D4A6D7CC',
  EC: '50269625-052D-4ADC-89D5CDF93194396E',
  EG: '7BA8542C-AC32-40FF-A6A2EDD27E6A506E',
  SV: '1BF4ECEF-E643-470F-92BCFD7DF5ADCBC5',
  ER: 'BB525AA8-1866-40A2-BF910AC65A4A60E4',
  EE: '1851B9DB-BB67-4BF0-ADC3E3360865E40C',
  ET: 'F04FBC07-DD60-466E-A6872717F632692F',
  FJ: '21A2DBEA-DACA-4B91-8C312676B72E070A',
  FI: 'C68B652F-40AF-4BC6-85882F28066028A1',
  FR: 'F63037F5-81D3-43C1-BDDA9E013C5AB299',
  GF: 'A957FD0C-B3D4-42FC-83ABFD58CBE96A53',
  PF: '386A798F-5C2F-4041-95E27D13DEA1CACE',
  GM: '7C089982-E625-4B6E-A06472C9DE42B4F3',
  GE: 'FCB5348D-CF62-48C9-8BE285D5B65D0A45',
  DE: 'A44F82B0-B0F7-435D-B542A003E62748B9',
  GH: '14B6EC60-7D28-4A75-8A5287538A8E22C3',
  TC: 'E0959774-0A66-4041-905D45CC36D30A90',
  GR: 'A16137B5-AB41-441F-A1646FB4631412B8',
  GD: '859EBF46-7154-4ACE-BB5EC6D86A233E31',
  GT: '84A26E90-19A3-4C1C-8FBA5A46DD892311',
  GN: '745AAC9F-EC52-4C91-B85B8EAA5A5E58BD',
  GY: '3E953D67-C692-45B1-9BEB436584342688',
  HT: 'C8202350-30D7-4A07-AB0074362145823A',
  HN: '424148FB-FE14-4DA8-A661E500EEAB4496',
  HK: 'F4177B68-01A5-4142-81E1970FBEA681CD',
  HU: 'CDDBD607-6264-4941-A5F16986994B0748',
  IS: 'F9145184-1044-47F4-B0FE5487C88ADD48',
  IN: '5297D9E2-1847-4BC4-81FBE569EB21C272',
  ID: '40F1B51C-BA3E-43E9-A598AC1D2105EBCA',
  IR: 'B789BBC3-FA0C-4859-9E9C7AE0BE70BB29',
  IQ: '2715996C-2849-4433-AC47FE6A7D75213A',
  IE: '25F83732-5D4E-49FF-B2B3548A29A2C359',
  IL: '867EFBFC-116A-4B07-A31CC9C9339FED09',
  IT: '9F0CAC5E-C3F5-4D73-97A6A58C66100503',
  CI: '66BFA1B5-6709-45CF-82F08C48C4055895',
  JM: 'AD4FADE3-82B1-4703-9E213F35678E234F',
  JP: '87B0144E-D709-4205-9349BF6DD4888283',
  JO: 'E29E30EF-197A-49DA-A15D7D5E32EF604F',
  KZ: '6B23252B-38E2-4955-BA126DF5068FB205',
  KE: '45F9B9C1-C48E-4935-A8D84B58F7920BA8',
  KW: '523BA6AD-7A2A-4F35-864ED92200AC1CFF',
  KG: '222F4501-8A4A-43AC-877FC84042A62851',
  LA: '476A3E66-53E8-46CA-8EE4AAD04BF9E823',
  LV: 'E693929B-7EA4-4022-A0AC2D6B82AAE81A',
  LB: '46F8AA4C-86B2-4C62-B38E6E523604E8D9',
  LY: 'D8F80966-29BF-492E-8ACABF09E77EB800',
  LT: '2A3468FF-3039-45DD-BE91E421730F8DA7',
  LU: '97B26BCA-C997-4206-83DEE49BEEFF9025',
  MK: '2FF89A7F-F2CE-4047-9BE78546DFF1FB13',
  MY: 'DFD617F2-89C0-4DA3-ADB1F6B9E9F3CEFF',
  MT: 'FBF81F12-F3EC-4071-90C90C05355FA00C',
  MQ: '6767D32A-1BFE-4FF9-8DD026F6DF647354',
  MU: '22F4928B-FAF2-425A-9966B23E61141153',
  MX: '1A991CB9-2B8C-49BE-832D4E6DBBB9DDA2',
  MD: '1EF2FA9C-F216-4DAD-9B76B13605192F12',
  MC: '70BFF203-7D52-498C-9BD93D519F3101E3',
  MN: '8DF6FD78-D484-48A0-89CED71D753670E9',
  ME: 'CD402303-8D67-427A-A5B7A68B5E3E978D',
  MS: '6919D4ED-ABB8-4EB3-B7F4F650659DAC82',
  MA: '9F09857D-B62D-4EE8-9E1BC88A85543678',
  MZ: 'C388ECB2-982D-42B2-9D8F35ABA5662537',
  NA: '815038A6-4496-43A5-BDCDE6DDEA18EF49',
  NP: '694EE945-3438-42AB-952BED4E42C03B4D',
  NL: '86012EF8-15B8-4120-BB050D18A1D0DE09',
  NC: '6BE6E5F5-7DB5-4599-B730BB0CD0490C29',
  NZ: '86C67A50-6463-4D42-A9A33D0CCDB5C218',
  NI: '3035516B-3977-45B8-89073B2E0374E084',
  NG: '8754751B-FDBE-4D66-8207E47A078B04A2',
  NO: '09FB5ED0-C0F2-45D1-B5F119396EAA6986',
  OM: 'A9DD52DB-6CFE-4E96-BB4319FD29EA027E',
  PC: '7AEE7C48-DD1D-409F-9D12C5843FEDB6A5',
  PK: 'F125FB21-79CB-40B1-A301410FE803C4A0',
  PA: '3F837849-1E20-4A3B-ADF0C8270A8BBBF1',
  PY: '66368DB4-2871-4FE8-B6B31437B946C225',
  PE: 'C2E45DB0-9980-4A93-ADF64EFDEB69E8A4',
  PH: '1F674ED0-996B-43FD-BEA078F13ACFC3BC',
  PL: '05E8C663-6CA8-44C0-98E3EFB977FAFD03',
  PT: 'ECEF7EF6-0422-4AF3-A14A54F247DD74FE',
  PR: '838C3264-3DF8-4001-963BB57B54F271CD',
  QA: '8D290E05-E384-4D6C-A4A2A0A41901BCEF',
  RO: '9D048018-B986-47BA-8045CBAE8D197657',
  RU: '90EB816F-B352-4369-A79448F7DB755292',
  SA: '0454DBD6-3C2B-43B6-ADAB64E15A27CB32',
  RS: 'CC34F4F4-326A-4D10-924FEA4F9768286E',
  SG: '3C99325B-3840-4BC5-BA2007578993057B',
  SK: 'DB00E834-0508-4FE9-A170E3658A33EA7D',
  SI: '7B6B518A-AE22-4BB9-9A2869042E8A9D23',
  SO: '10832094-D5FE-4FEA-85B3A07FC575D542',
  ZA: 'A8250594-6078-4BF2-88F54AC22F12F122',
  KR: 'F1DB707E-5158-4821-BA3C03956D5AF610',
  ES: '8B3024C7-C0E9-4439-B5631D30C3A9F658',
  LK: '5AEF77EC-56D8-43FF-99029ED57E27CCDB',
  BL: 'D68E6072-0622-4BBC-B92984D91C1510E3',
  KN: '770C72F6-66B5-4C90-95DF502CB5658F9B',
  LC: '460C4456-4485-4EB2-88B4EACD276935A3',
  MF: 'CBF13199-8EFE-4801-A82A642DF22B89A1',
  VC: 'BE9E9B09-6732-45EC-92141268A766750C',
  SD: 'CD33F5B1-BCF1-4081-8C97BBE50F2EF5EB',
  SR: '038DB887-0095-4235-B26FDEC473DE7BCD',
  SE: 'FB2828B7-9BB7-440A-978A9E9AC11FDC08',
  CH: '4FDDB899-CA7C-477A-B9F560707E8F54CF',
  SY: '3887A06A-3205-48A8-9D5663BD9EAE2D96',
  TW: '355DC9D9-BFCA-45D2-975C20877BDB537E',
  TJ: '16A788A2-14E8-4FE3-899CE59DE816C30A',
  TZ: '37394FDC-7F9D-4FEF-A9FE2EDA1F7D2911',
  TH: '719259AD-3343-4C8A-B028A7910EEBD24C',
  VG: 'C65C7A6C-ACF7-4CEE-B8744B13CB2ED319',
  TT: '2D0E5BE8-A8F6-491D-8BF06F54C65F3C82',
  TR: '4A6BE7CD-075B-423C-B0CA8621ADA1BD98',
  TM: '120089BB-2F86-48A8-BE62AC5124F872F5',
  VI: '6C0CF067-DF50-4E07-80B69CCF8B374455',
  UG: 'B5940151-2FFB-4887-A5C4F897DB2D5706',
  UA: 'F46CB21C-D1FF-4D96-AF0E4EF22C0E3F4C',
  AE: 'B92894DF-0E4D-431B-A921054807FF4FDF',
  GB: 'A18538BF-16EB-422A-8D03C56B0EB5677F',
  US: '08241095-DEAE-45D1-A39344A3B26AC903',
  UY: 'EFFD66F1-6A48-4999-998D414ABE4F3EAD',
  UZ: 'EF34652E-E07F-460D-A2C2524AAA0B6EA7',
  VE: 'D01F5737-5BA6-4BEE-B89530AB31A4EDBB',
  VN: 'CDE653FE-9654-44C9-92FFC54EFE9255E2',
  YE: '2E19848C-FB65-42FD-B6ED44EE7C221949',
  CD: '7F643C75-7DE5-4E6A-943C4A22293A651E',
  ZM: 'E1FF0680-C6EA-4C09-BC18BEBD4954502E',
}

const BYNDER_COUNTRY_OPTIONS_QA: Record<string, string> = {
  AF: '8A43F076-5581-49AD-9DC4A626B1D28304',
  AL: '811EC775-5210-4B45-8A3187C65B7539BC',
  DZ: '05FD81DA-1070-474F-894759DEFACBC3D9',
  AO: '1E12E21C-24EE-4E1B-84F956F0DCDE70F5',
  AI: '75CFE2C1-3F62-496B-85C258F0F92E82B1',
  AG: 'F1D08E94-B91D-4DF8-B44471C3D7FFF30E',
  AR: 'ED9B1FB7-83E6-475A-9F15BA3D25BE7125',
  AM: '857670C5-9042-4FFB-93FD1E4399E5DF5F',
  AW: '886DB2FD-B5B2-4FD0-9C94C85CA4D1F6A4',
  AU: 'A363E67D-A9E5-4988-8F6C34D45B53ADE7',
  AT: 'A38848E4-6AF4-4757-99A08CEB1BE95558',
  AZ: 'C223D496-36AE-414C-BF4EE703CD7D4804',
  BS: 'CA67C4AF-D305-4E59-B383102B914D6EE0',
  BH: '54DA2D9E-7A80-41BB-968B98AA6B13715F',
  BD: '6EE7DFE9-772C-41F1-87FE548D069BAD71',
  BB: '43CB3260-E108-4124-B6DB1F7BD10AC0D7',
  BY: '3CB00C27-66D2-48DF-892DA21A4D5AF66C',
  BE: '811C9C83-83B9-4449-81AEDC0FB6C30261',
  BZ: '77A44A52-C240-4F23-89048B860202D35E',
  BM: 'A5056771-3670-47DE-AA9EF38B710CA8C4',
  BO: '8C808364-2839-40ED-8A477A26B048CC3A',
  BA: '82C82067-9D51-4D4A-A04706D2944EE704',
  BR: '7A8C91A7-1089-4B38-B8ECD2459A787A4A',
  BN: '0525B2E7-E477-4ED5-868676BC8EBE795B',
  BG: '3654DEDA-98C8-4FCC-8A9EFE074A3F34F7',
  MM: 'C06E7682-8576-41AB-A352F84E1A7F5FCB',
  KH: '56552A39-E397-448F-9F0789CF58B0781D',
  CM: '15D6533B-83B3-43DD-80BB5372DB8921D1',
  CA: '3ABA4AAC-7602-4DE3-826644433D801FF5',
  KY: 'E17013E2-D4EC-4582-954F2EC128A739F8',
  CL: 'FCAD67CE-FAE1-40E1-947A61038C644F7A',
  CN: '5E2F3333-D431-4DC5-8DCE03F1B6FEAEAF',
  CO: 'DE55C250-48A0-45CF-80B330CE90EA76AC',
  CR: '4287D3AB-F3FE-4F83-A7F66129366DA48E',
  HR: '619F67AB-D019-4032-87AF76728E74465F',
  CW: '296CFA16-A6A8-4A1A-AD270C9F04A89F69',
  CY: '65839AA6-9BA3-4E6E-B718C7ECFB98080F',
  CZ: '7C69928F-807D-4F38-926D9FA35E31A20A',
  DK: 'E853F5A7-7A66-46E5-8D4800C685606E37',
  DJ: '260CACA1-571D-48F0-BADF0F3AAFDD953E',
  DM: '316ECC47-64AC-4B7F-82065542D3AA3928',
  DO: 'F4753D42-D19A-472D-AFA48B0D44FBE4C1',
  EC: '27268FF1-B8B4-4205-87B0B66865EE8733',
  EG: '95F923D8-4779-4115-8DF5081E22CC36A2',
  SV: '3DEBAC75-53D4-437C-960DDA2E3974D749',
  ER: '07E34765-7560-485E-920A334073B97422',
  EE: '15F564B2-C3BC-415E-998C6BF83E838E24',
  ET: 'ADF93E0D-8D3E-4ADD-BA62CF227896D450',
  FJ: 'DD29918B-4987-40E5-ACF73CCBE5F3222F',
  FI: 'AB54F8AD-C6D5-4016-9CD54B12AE5E124F',
  FR: '9216B7F4-8B0A-42F5-A1883D44F0A446A7',
  GF: '3D6FD496-6368-441A-ACB3CB3A99131AAE',
  PF: 'B2F1899B-BDB3-46CF-9E80654FAEDDC1E6',
  GM: '90E1BDF9-66CD-4257-839D0B31FA1EC1E7',
  GE: 'A5F927A2-9C09-447A-82A3E2B9C270751B',
  DE: '791F82EF-374C-412A-9D3CE20C4AD7DF37',
  GH: 'F0330AFC-3CBC-4898-BC244EB656473779',
  TC: '2C9DE56D-E20B-4DBA-B0E7AA8127A12E48',
  GR: '882BDBD9-9091-47E8-B929266D1877E9C8',
  GD: '0BEE035D-DDD6-4AB0-806D70B4EC539CAF',
  GT: 'ECA1C9DF-957B-462E-89FFA17CB556E6A5',
  GN: '02BC4BB4-846A-4DE3-A08BA68C924E9246',
  GY: '051B4D5B-A2BA-49AC-A1F07E0F8718D655',
  HT: 'B161197B-3C5D-45EE-9B426EF3B7ED038E',
  HN: '5448A319-C06A-433C-8181AEDED6FF12C5',
  HK: 'CE338299-B845-49CE-8DB16BA968FBD002',
  HU: '57145BB3-9C81-4A95-8527A8C6F79C30F5',
  IS: '0B038A59-2D8A-45F6-83ED9528AF106ECF',
  IN: 'F7B503B2-5860-4449-B5A6A3E8E8163A18',
  ID: 'DAE4571F-AA10-4BC9-996AC52ED35A9AF7',
  IR: '3C808F95-A0EA-4616-B19392933FEFDAC7',
  IQ: 'E7A55CB5-3F99-41B4-B6EE634D7E5885EF',
  IE: 'AC904429-3314-4E4C-9B7DDA90D9FA1139',
  IL: '0798794C-68FD-476F-8D915D0E1C32C0CC',
  IT: '0FE08834-FAC8-4A2E-B17B40081BAF43AA',
  CI: '5C64D896-ABBD-4F31-8C8F2731259E31FB',
  JM: '1FBBB537-9E83-4098-AEADE3177A45FC3D',
  JP: 'F2470B1E-5FA3-4A53-AB797FC3E902BD45',
  JO: 'AB0429F0-ABE0-4CD6-91E791AF4C5F2984',
  KZ: 'DD292CA9-745C-41C9-9EE948A3E05E29C3',
  KE: 'C0C73077-CC88-4A26-B0C7B68715202915',
  KW: 'F71A147A-17CB-4D25-9A7ADC32A095C56B',
  KG: 'F280CA9B-A123-49F2-81BCB2727DEEAB8B',
  LA: '69EA806F-1AA7-4BA3-9F9532AE592DD8F0',
  LV: 'DDBDF08E-855F-4646-8316D8CF9BD237B7',
  LB: 'DBDEC84F-921C-4B0C-9B45D3AE44011662',
  LY: '33069DA0-6828-418F-B630F7406D416490',
  LT: 'B7CB9EE1-15F1-4A75-990D66C547364A93',
  LU: '40C07BB2-5E92-4F31-B07864FAEC40EC6E',
  MK: 'F043EC7E-3875-41AC-A1795564503FA256',
  MY: '109D02DF-5D2D-40A0-8C4D2B4F8DEDA27E',
  MT: 'EE1B9437-EE7C-4003-881601A7B8BCC91A',
  MQ: '07004C1C-8E22-47DD-8613E120AAE59854',
  MU: '0DD93509-C26D-4F14-8D96D52721B2776C',
  MX: '382035FF-A742-42D5-B88D1DDD9E519C3D',
  MD: '609DA351-8892-4AF5-97C50B84A58A4E71',
  MC: '6D0ECEE7-C625-48C3-91FD87C9C299B234',
  MN: '65FBC11B-7A7D-4687-9B9E89917C70C7E1',
  ME: 'BAB72891-74ED-4234-A140FE9AC32661B0',
  MS: 'CC8B2264-1CBB-4547-A55311AC74EDF72B',
  MA: 'D87FAC6B-9245-49A5-BB7554FBB25391E4',
  MZ: 'A3694D71-93F2-465D-AB196FAE734AA6DE',
  NA: '07205B40-2C4E-4D3D-B8ADFDDC5F7E2E17',
  NP: 'B7070ECA-BE97-4CF3-AA3C93507F3BDCAB',
  NL: '520B1EB3-D957-4023-9094CC79F6616DEE',
  NC: 'FE42E425-2998-4FF5-8D7916F837F8DB61',
  NZ: 'DAEB344A-6FD0-4700-A6B731BEF45F82CD',
  NI: 'EC7BFE82-C520-4CD2-BCCE5F8D4FD14C36',
  NG: '984A86F7-7882-4A2E-83CB2E2E1662F2CA',
  NO: 'EBCBB1C4-9A01-4649-8D3280D4C4CE1254',
  OM: 'AC61D834-2F9A-47AA-900188E1D87C67BC',
  PC: 'E0FD8A01-32C7-437F-835805C28644F57D',
  PK: '33A40F5B-CDE4-44B6-B10DA0DEC9BE8E19',
  PA: 'ED1B518A-A6EB-4451-A759A89144D8DD41',
  PY: '185893F9-5F8D-438D-A4318D328F7EB7DC',
  PE: 'F09DB97F-B739-4C4A-80C93035CF832375',
  PH: '98A368B1-E6D2-455B-9B19B23304B939A9',
  PL: 'D295C45E-CAF1-42B0-86037DDB19FDB546',
  PT: '63E92150-D0E2-45FC-95017709596C40CE',
  PR: '48028A65-9326-437B-A637F5F6B35B2D8B',
  QA: '47E46DA4-FAEE-4C53-8BD26E95EB219B83',
  RO: '66DE785B-16F5-4258-9E012622FECAE4DB',
  RU: '46421F4D-D426-472E-8EC9F847E534F38E',
  SA: '98902D35-BE96-4073-B6AFBC2838059E4B',
  RS: '2576C79F-9014-4433-8EB9E358C2C34576',
  SG: '6762C8D5-65F8-4FC3-9621CA8677468D81',
  SK: 'F945E1A0-0F94-48CE-8CB912DF4E0EE0A4',
  SI: '4BF02EAA-0DD7-464E-A815687C75E372D2',
  SO: '01C4DCA3-C4A2-4D7C-BCE919086E63FBCE',
  ZA: '3F00EF24-4411-4B09-A93A579F44073484',
  KR: '1F12F61D-B235-4ED0-966EF0AC939DCEA6',
  ES: '6BB577E6-BF23-44F1-83632F10C3AF6ACC',
  LK: '8052A807-03A0-4126-A097AEEBC99E2557',
  BL: '69F4CD1D-927A-47A2-A3E0FA7B276597A0',
  KN: 'CCE2E090-2894-46C3-9B8863E6411D982D',
  LC: '60AFB7A9-FD45-4C7C-A9F2DED743D45440',
  MF: '18276EEF-4BA7-42E8-924A955920C4C5E1',
  VC: '5B59FE99-7EA1-4C99-A91FCD5718D6E9CC',
  SD: '0F947E3D-2BC7-4DAD-9245FA37E3AA545F',
  SR: '8885D440-9DCE-46EE-A645CB5BFD5203CB',
  SE: 'EF22FAC7-8B18-47A2-8F46B145E0FE5E5D',
  CH: '2044E8DD-B592-4B15-8670000FFB1E4B96',
  SY: '99A74D87-6A07-4FC0-99EACA93FA4B62A2',
  TW: '5B40E68B-DFBB-4715-9D95896D8C87529F',
  TJ: '0B407824-3F80-4230-A5115D0E1E9BE1E0',
  TZ: '231615E3-C403-4E03-8B6024220496B638',
  TH: '86C40A52-D1D0-4D65-87143DE9C27E57AF',
  VG: '72C70BE1-3800-4E8F-98E02236C68C270F',
  TT: '8A8E0A51-8E69-448C-A5FE1984E0E0D487',
  TR: 'EF85B5D9-ACC9-4DC1-AF1CBF9D1E00E175',
  TM: '3CA1E8D8-9FE4-4926-A390C39A5A8AB087',
  VI: '1CB5DA8B-63C7-4490-A8D0801B8F1EC9EF',
  UG: 'C682AD45-4346-4DAB-A357B2E52E426353',
  UA: '67D9D02F-52F2-411C-88AE9DEFFB484B42',
  AE: '1ED5B772-A766-41BE-83E0031DE688389A',
  GB: '0671C07F-73DF-4F8C-A1723C1C6D699294',
  US: 'A27C1AEC-7033-4092-8507090634D0609E',
  UY: 'D9940970-0621-4796-9BD777696AD99E89',
  UZ: '8A11256A-B968-4897-949BC693D4D49CF2',
  VE: '455D52BD-DCD0-45B3-A0B269B26B572213',
  VN: 'C1B33955-220F-4FD5-9773C6AAC01D2ED2',
  YE: 'AB05B5C7-06B7-4FAA-AA1D3341E4C03762',
  CD: 'CF1C2173-8C69-47ED-81ECF6EF300FC38D',
  ZM: '41DA48F7-0A86-48C5-87305EFA7909449B',
}

const BYNDER_COUNTRY_ENV_MAP: Record<string, Record<string, string>> = {
  prod: BYNDER_COUNTRY_OPTIONS_PROD,
  qa: BYNDER_COUNTRY_OPTIONS_QA,
}

export const BYNDER_COUNTRY_OPTIONS = BYNDER_COUNTRY_ENV_MAP[bynderEnv] ?? BYNDER_COUNTRY_OPTIONS_PROD
